// src/lib/nav.js
// STATUS: active | UPDATED: 2026-10-02
// LOG:
//   2026-10-02  Notes and his manager system back in Jazz's menu, because he
//               asked for both. See the note above AUTHOR_KEYS.
//   2026-09-17  Notes into the operator list, first. Jazz could only reach
//               it through Admin.
//   2026-09-17  'Running the floor' for manager and up. The manager layer
//               existed as content in the repo and in no menu.
//   2026-08-30  New. Navigation is role aware. It was not, and every role saw
//               the same product.
//
// What each of the four levels sees, in one place.
//
// THE BUG THIS FIXES
//
// Every role saw an identical sidebar. A salesperson saw a "Team" tab, opened
// it, and found a roster containing exactly themselves, because RLS correctly
// refused to show them anybody else. The data layer was right and the navigation
// was lying about what the product is for.
//
// That is worse than it sounds. A salesperson does not need to know a manager
// surface exists, and a manager does not need the operator tools. Showing them
// makes a simple product feel like a confusing one.
//
// THE NAVIGATION IS NOT THE PERMISSION MODEL
//
// Hiding a link is not security and is not treated as such anywhere. RLS decides
// what data a person can reach, ProtectedRoute decides which routes render, and
// this file decides only what is worth offering. If these three ever disagree,
// RLS wins and the person sees an empty page rather than somebody else's floor.
//
// LABELS CHANGE WITH THE ROLE, ON PURPOSE
//
// The same route is called different things depending on who is looking, because
// a manager thinks in floors and an owner thinks in stores. /team/ is "My floor"
// to a manager and "My people" to an owner. Same page, honest name.

export const LEVELS = {
  admin: 0,     // Jazz and Tyler. Every store. dealership_id is null.
  owner: 1,     // Owner or GM. Their whole store.
  manager: 2,   // Sales manager. Their floor.
  employee: 3,  // Salesperson. Themselves.
}

// Lower number means more reach. `atLeast('manager')` includes owner and admin.
export function rank(role) {
  return LEVELS[role] ?? 99
}

export function atLeast(role, minimum) {
  return rank(role) <= rank(minimum)
}

// `minimum` is the least senior role that should see the item. Absent means
// everybody. `labels` overrides the label for specific roles.
const ITEMS = [
  { key: 'today', to: '/dashboard/', label: 'Today', icon: 'LayoutDashboard' },
  { key: 'training', to: '/courses/', label: 'Training', icon: 'Map' },
  { key: 'coach', to: '/coach/', label: 'Coach', icon: 'Sparkles' },
  { key: 'calendar', to: '/calendar/', label: 'Calendar', icon: 'Calendar' },
  {
    key: 'floor',
    to: '/manager/',
    label: 'Running the floor',
    icon: 'ClipboardCheck',
    minimum: 'manager',
  },
  {
    key: 'team',
    to: '/team/',
    label: 'Team',
    icon: 'Users',
    minimum: 'manager',
    labels: { manager: 'My floor', owner: 'My people', admin: 'Team' },
  },
  // His first daily discipline. Above the one on one because that is the order
  // of a day: the floor meets, then the manager sits down with people.
  {
    key: 'morning',
    to: '/manager/morning/',
    label: 'Morning meeting',
    icon: 'Sun',
    minimum: 'manager',
  },
  // The one on one, which is the first of his management artefacts that comes
  // back filled in rather than printed. Its own entry rather than a link buried
  // inside /manager/, because the list of promises nobody has gone back to
  // check is a daily thing and /manager/ is a reference page.
  {
    key: 'oneonone',
    to: '/manager/one-on-one/',
    label: 'One on ones',
    icon: 'MessageSquare',
    minimum: 'manager',
  },
  // Owner and above. The five numbers Jazz asks a store to report, and the only
  // place in the product that touches the fourth leg of the governing test.
  //
  // In the menu from the day it shipped, because the two features above it were
  // each complete and unreachable for weeks: /manager/ held a 28KB extract
  // nothing imported, and /admin/onboard/ was a finished flow you could only
  // reach by typing the URL. A feature nobody can find is a feature nobody has.
  {
    key: 'numbers',
    to: '/numbers/',
    label: 'The numbers',
    icon: 'BarChart3',
    minimum: 'owner',
  },
]

// Level 00 only. Kept separate because it is a different job, not a deeper
// version of the same one, and it renders under its own heading.
const OPERATOR = [
  // First, because it is the one Jazz is meant to open. It was reachable only
  // via Admin, which is two clicks and a section he has no other reason to
  // visit. Same lesson as onboard below: a feature nobody can find is a
  // feature nobody has.
  { key: 'notes', to: '/notes/', label: 'Notes', icon: 'FileText' },
  { key: 'admin', to: '/admin/', label: 'Admin', icon: 'ShieldCheck' },
  // /admin/onboard/ has existed as a complete flow since 1 September and was
  // routed but in no menu, so the only way to reach signing a new store was to
  // type the URL. A feature nobody can find is a feature nobody has.
  { key: 'onboard', to: '/admin/onboard/', label: 'Add a dealership', icon: 'Store' },
  { key: 'digital', to: '/admin/digital/', label: 'Janda Digital', icon: 'TrendingUp' },
  { key: 'everybody', to: '/admin/team/', label: 'Everybody', icon: 'Users' },
]

// WHAT JAZZ SEES, WHICH IS ALMOST NOTHING.
//
// He is `is_method_author`, and his whole relationship with this product is a
// conversation with the coach. He dictates every message, he has never opened
// the notes board, and 39 manager lines sat behind a button for five days
// because a page is not a surface he visits. See docs/COACH.md.
//
// Tyler, 2026-09-24: "make it look like a clean admin view for Jazz with no
// notes. We're going to talk more in human and interact with the coach only."
//
// So he gets three items. Today, Training, Coach.
//
// NOTHING IS TAKEN AWAY FROM HIM. He is still Level 00 and every route still
// admits him, so any URL he types works exactly as before. This is the MENU,
// and a menu is a recommendation rather than a permission. If he ever wants
// the manager review page he can be sent a link and it opens.
//
// Tyler keeps all of it, because he is the one who reads what came back.
const AUTHOR_KEYS = ['today', 'training', 'coach']

// TWO MORE, BECAUSE HE ASKED. On 2026-09-27, in the coach: "i am supposed to go
// into the notes section of the academy but i don't see it on the side any
// longer. am i missing something?" And twenty minutes later, about his own
// manager system: "how can i see what you're seeing? i would like to audit that
// to be sure." The three item view was Tyler's call for a man who never opened
// a page. He then went looking for two, so the menu follows him. The manager
// system page shows all 39 lines of his Trainer Edition with how each was
// settled, and he can correct any of them there.
const AUTHOR_EXTRAS = [
  { key: 'notes', to: '/notes/', label: 'Notes', icon: 'FileText' },
  { key: 'mysystem', to: '/admin/manager-review/', label: 'Your manager system', icon: 'ClipboardCheck' },
]

export function navFor(role, { author = false } = {}) {
  const items = ITEMS
    .filter((item) => !item.minimum || atLeast(role, item.minimum))
    .map((item) => ({ ...item, label: (item.labels && item.labels[role]) || item.label }))
  if (!author) return items
  return [...AUTHOR_KEYS.map((k) => items.find((i) => i.key === k)).filter(Boolean), ...AUTHOR_EXTRAS]
}

// The author gets none of this. Notes, Admin, onboarding, Janda Digital and
// the full user list are all Tyler's job, and every one of them is a screen
// Jazz has either never opened or has no reason to.
export function operatorNavFor(role, { author = false } = {}) {
  if (author) return []
  return role === 'admin' ? OPERATOR : []
}

// The three tabs on a phone. A salesperson lives here, so it stays the same
// three for everybody: the thing a manager does on a phone between customers is
// sign somebody off, and that lives inside the floor rather than on its own tab.
// Adding a fourth tab to a 375px bar costs every role legibility to serve one.
export function mobileNavFor(role, { author = false } = {}) {
  const base = ['today', 'training', 'coach']
  const items = navFor(role, { author })
  if (author) return base.map((k) => items.find((i) => i.key === k)).filter(Boolean)
  const picked = base.map((k) => items.find((i) => i.key === k)).filter(Boolean)
  if (atLeast(role, 'manager')) {
    const team = items.find((i) => i.key === 'team')
    if (team) picked.push(team)
  }
  return picked
}

// What this person's landing page should lead with. The dashboard is the same
// route for everybody and should not read the same way to all four.
export function landingIntent(role, { author = false } = {}) {
  // The author is not an operator. He shares the role and not the job, and a
  // dashboard of build status is the wrong first screen for the man whose
  // method this is. `method` leads him back to the conversation.
  if (author) return 'method'
  if (role === 'admin') return 'operator'
  if (role === 'owner') return 'store'
  if (role === 'manager') return 'floor'
  return 'self'
}
